import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import TicketSidebarSlot from "../PluginBridge/slots/TicketSidebarSlot";
import api from "../../services/api";
import { PluginRuntime } from "@markedesk/plugin-sdk/ui";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { DraftInsertProvider } from "../../context/DraftInsert/DraftInsertContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { contactsContract, SOCKET_EVENTS } from "@markedesk/core-contracts";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  // Chat + painel lateral de plugin (ticket-sidebar) lado a lado. A margem
  // negativa que reserva o espaço do ContactDrawer fechado mora AQUI, não no
  // chat: com o painel ao lado, a margem no chat o faria entrar por baixo dele.
  contentWrapper: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    display: "flex",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },

  contentWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  mainWrapper: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [dropProcessingReset, setDropProcessingReset] = useState(0);
  const [presence, setPresence] = useState(null); // presença do contato (digitando/online)
  const presenceTimerRef = useRef(null);

  // Expõe o ticket aberto (+ contato) no store central para os plugins.
  useEffect(() => {
    PluginRuntime.setActiveTicket(ticket?.id ? { ...ticket, contact } : null);
    return () => PluginRuntime.setActiveTicket(null);
  }, [ticket, contact]);
  const { companyId } = user;

  useEffect(() => {
    if (isNil(ticketId) || ticketId === "undefined") return;

    setLoading(true);
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/u/" + ticketId);

        setContact(data.contact);
        setTicket(data);
        if (["pending", "open", "group"].includes(data.status) && data.status !== tabOpen) {
          setTabOpen(data.status);
        }
        setLoading(false);
      } catch (err) {
        history.push("/tickets");
        setLoading(false);
        toastError(err);
      }
    };
    fetchTicket();
  }, [ticketId, user, history]);

  useEffect(() => {
    // Socket pode ser nulo (logout forçado, instante antes de conectar): sem
    // isto o socket.on lançava e derrubava a tela inteira (11-09-2026).
    if (!socket) return undefined;
    if (!ticket || !ticket.id || !ticketId || ticketId === "undefined") {
      return;
    }

    if (user.companyId) {
      const ticketUuid = ticket.uuid;
      const ticketNumericId = ticket.id;

      const onConnectTicket = () => {
        socket.emit("joinChatBox", `${ticketUuid}`);
      }

      // Join imediatamente (não apenas no reconnect)
      socket.emit("joinChatBox", `${ticketUuid}`);

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticketNumericId) {
          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticketNumericId) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      };

      // Presença do contato (digitando/online). Casa pelo número do contato e
      // auto-expira: presença é efêmera; sem novo evento em 8s, some.
      const onPresence = (data) => {
        const num = ticket.contact?.number;
        if (!num || String(data?.number) !== String(num)) return;
        if (presenceTimerRef.current) clearTimeout(presenceTimerRef.current);
        if (data.state === "paused" || data.state === "unavailable") {
          setPresence(null);
          return;
        }
        setPresence(data.state); // composing | recording | available
        presenceTimerRef.current = setTimeout(() => setPresence(null), 8000);
      };

      socket.on("connect", onConnectTicket)
      socket.on(SOCKET_EVENTS.TICKET.LIST, onCompanyTicket);
      socket.on(contactsContract.events.changed, onCompanyContactTicket);
      socket.on(`company-${companyId}-presence`, onPresence);

      return () => {
        socket.emit("joinChatBoxLeave", `${ticketUuid}`);
        socket.off("connect", onConnectTicket);
        socket.off(SOCKET_EVENTS.TICKET.LIST, onCompanyTicket);
        socket.off(contactsContract.events.changed, onCompanyContactTicket);
        socket.off(`company-${companyId}-presence`, onPresence);
        if (presenceTimerRef.current) clearTimeout(presenceTimerRef.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, ticketId, ticket?.id, history]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleQuickMessageSelect = (quickMessage) => {
    try {
      if (quickMessage.message) {
        // Disparar evento que o MessageInput vai escutar
        const event = new CustomEvent('insertQuickMessage', {
          detail: { message: quickMessage.message }
        });
        window.dispatchEvent(event);
        
      }
      
      if (quickMessage.mediaPath) {
        // Tratar mídia se necessário
      }
    } catch (error) {
      console.error("Erro ao inserir resposta rápida:", error);
      toastError("Erro ao inserir resposta rápida");
    }
  };

  const renderMessagesList = () => {
    if (!ticket || !ticket.id) return null;

    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          dropProcessingReset={dropProcessingReset}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          ticketStatus={ticket.status}
          openTicketId={ticket.id}
          openTicketQueue={ticket.queue}
          openTicketQueueId={ticket.queueId}
          // #4292 — so vai para a conversa o que o atendente marcou como fixado
          // naquele contato; sem marcar, a observacao vive no drawer e no botao.
          contactNotes={contact?.notesPinned ? contact?.notes : null}
          contactNotesExpanded={!!contact?.notesExpanded}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          onDroppedFilesConsumed={() => {
            setDragDropFiles([]);
            setDropProcessingReset(prev => prev + 1);
          }}
          contactId={contact.id}
          contactName={contact.name}
          whatsappId={ticket.whatsappId}
          ticketUpdatedAt={ticket.updatedAt}
        />
      </>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <div
        className={clsx(classes.contentWrapper, {
          [classes.contentWrapperShift]: drawerOpen && !isMobile,
        })}
      >
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen && !isMobile,
        })}
      >
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {/* #4292 — o gate olha o CONTATO em estado, nao `ticket.contact`:
              nem todo emissor de socket manda o ticket com contato dentro
              (ex.: mensagem automatica de transferencia), e nesses casos o
              cabecalho inteiro desmontava. */}
          {contact?.id !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                presence={presence}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
        <TicketActionButtons
          ticket={ticket}
          contact={contact}
          onQuickMessageSelect={handleQuickMessageSelect}
        />
        </TicketHeader>
        {/* </div> */}
        <Paper>
          <TagsContainer contact={contact} />
        </Paper>
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {/* Deixa algo fora da caixa de digitação (um plugin que desenha
                  botões na bolha) oferecer texto ao rascunho do atendente. */}
              <DraftInsertProvider>
                {renderMessagesList()}
              </DraftInsertProvider>
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      {/* Painel lateral de plugin: só no desktop e com ticket carregado. */}
      {!isMobile && ticket?.id && (
        <TicketSidebarSlot
          ticketId={ticket.id}
          contactId={contact?.id}
          channel={ticket.channel}
          whatsappId={ticket.whatsappId}
        />
      )}
      </div>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

    </div>
  );
};

export default Ticket;